import { Button, useNotify } from "react-admin";
import {
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  makeStyles,
} from "@material-ui/core";
import React, { useState } from "react";
import { StyledButton } from "../../../components/ListShowButton";
import { useField } from "react-final-form";
import { find, findIndex } from "lodash";
import AddressAutoCompleteInput from "./AddressAutoCompleteInput";
import {
  removeSchoolLocationFromRouteLocation,
  reOptimiseRouteLocation,
} from "../tools";
import AiLoading from "./AiLoading";

const useStyles = makeStyles({
  dialog: {
    "& .MuiDialog-paper": {
      overflowY: "unset",
      width: "100%",
    },
  },
});

interface ChangeLocationButtonProps {
  order: number;
  disabled?: boolean;
  school: ISchool;
  busSchedule: IBusSchedule;
  maxStopTime: number;
}

const ChangeLocationButton: React.FC<ChangeLocationButtonProps> = ({
  order,
  disabled,
  school,
  busSchedule,
  maxStopTime,
}) => {
  const [open, setOpen] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedRouteLocation, setSelectedRouteLocation] = useState<any>(null);

  const routeLocationsField = useField("routeLocations");
  const routeGeometriesField = useField(`routeGeometries`);

  const newAddressField = useField("newAddress");
  const locationDataField = useField("locationData");

  const classes = useStyles();
  const notify = useNotify();

  const handleOpenDialog = () => {
    const findRouteLocation = find(routeLocationsField.input.value, [
      "order",
      order,
    ]);
    setSelectedRouteLocation(findRouteLocation);
    setOpen(true);
    newAddressField.input.onChange(null);
    locationDataField.input.onChange(null);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleSaveChangeAddress = async () => {
    try {
      setIsLoading(true);

      const { BLK_NO, BUILDING, LATITUDE, LONGITUDE, POSTAL, ROAD_NAME } =
        locationDataField.input.value;

      const newAddressRouteLocation = {
        ...selectedRouteLocation,
        lat: LATITUDE,
        lng: LONGITUDE,
        address: `${BLK_NO} ${ROAD_NAME} ${BUILDING} ${POSTAL}`,
      };

      const findRouteLocationIndex = findIndex(
        routeLocationsField.input.value,
        ["order", selectedRouteLocation.order],
      );

      const routeLocationWithoutSchool = removeSchoolLocationFromRouteLocation(
        [
          ...routeLocationsField.input.value.slice(0, findRouteLocationIndex),
          newAddressRouteLocation,
          ...routeLocationsField.input.value.slice(findRouteLocationIndex + 1),
        ],
        busSchedule.type,
      );

      const optimisedRouteLocations = await reOptimiseRouteLocation(
        school,
        busSchedule,
        maxStopTime,
        routeLocationWithoutSchool,
      );

      routeLocationsField.input.onChange(optimisedRouteLocations);
      routeGeometriesField.input.onChange([]);
      setIsLoading(false);
      handleClose();
    } catch (error) {
      notify("Failed to change address route location", "error");
      setIsLoading(false);
    }
  };

  return (
    <>
      <StyledButton
        variant="outlined"
        onClick={handleOpenDialog}
        disabled={disabled}
      >
        Change Address
      </StyledButton>

      <Dialog
        open={open}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        className={classes.dialog}
        maxWidth="sm"
      >
        <DialogTitle id="alert-dialog-title">
          Change Address of {selectedRouteLocation?.address}
        </DialogTitle>
        <DialogContent>
          <AddressAutoCompleteInput
            label="Select Address"
            source="newAddress"
            referenceSource="locationData"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="secondary" label="Back" />
          <Button
            onClick={handleSaveChangeAddress}
            color="primary"
            label="Save"
            startIcon={
              isLoading && (
                <CircularProgress size={20} style={{ color: "#FFFFFF" }} />
              )
            }
          />
        </DialogActions>
      </Dialog>

      <Dialog
        open={isLoading}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        maxWidth="md"
      >
        <AiLoading isLoading={isLoading} />
      </Dialog>
    </>
  );
};

export default ChangeLocationButton;
